import { getAPIURL } from "./config";

/**
 * Список соглашений с пагинацией и фильтрами
 * @param {Object} params - Параметры запроса
 * @param {number} [params.project_id] - Фильтр по проекту
 * @param {string} [params.number] - Фильтр по номеру
 * @param {string} [params.status] - Фильтр по статусу
 * @param {string} [params.date_from] - Фильтр по дате начала
 * @param {string} [params.date_to] - Фильтр по дате окончания
 * @param {string} [params.performer_type] - Фильтр по типу исполнителя
 * @param {number} [params.company_id] - Фильтр по компании
 * @param {string} [params.ordering] - Сортировка (префикс "-" для убывания)
 * @param {number} [params.page=1] - Номер страницы
 * @param {number} [params.page_size=20] - Размер страницы (1-100)
 */
export const getAgreements = async (params = {}) => {
  const searchParams = new URLSearchParams();

  if (params.project_id != null) {
    searchParams.set("project_id", params.project_id);
  }
  if (params.number != null) searchParams.set("number", params.number);
  if (params.status != null) searchParams.set("status", params.status);
  if (params.date_from != null) searchParams.set("date_from", params.date_from);
  if (params.date_to != null) searchParams.set("date_to", params.date_to);
  if (params.performer_type != null)
    searchParams.set("performer_type", params.performer_type);
  if (params.company_id != null)
    searchParams.set("company_id", params.company_id);
  if (params.ordering != null) searchParams.set("ordering", params.ordering);
  searchParams.set("page", String(params.page ?? 1));
  searchParams.set("page_size", String(params.page_size ?? 20));

  const url = `${getAPIURL()}agreements/?${searchParams.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    let detail = text;
    try {
      const json = JSON.parse(text);
      detail = json.detail || (Array.isArray(json.detail) ? json.detail.map((d) => d.msg).join(", ") : text);
    } catch (_) {}
    throw new Error(detail);
  }

  return response.json();
};

/**
 * Получение соглашения по id (детали, этапы, платежи)
 * GET /agreements/{id}  — без слэша в конце, чтобы не было редиректа и CORS
 */
export const getAgreement = async (id) => {
  const response = await fetch(`${getAPIURL()}agreements/${id}`, {
    method: "GET",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    let detail = text;
    try {
      const json = JSON.parse(text);
      detail = json.detail || (Array.isArray(json.detail) ? json.detail.map((d) => d.msg).join(", ") : text);
    } catch (_) {}
    throw new Error(detail);
  }

  return response.json();
};

/**
 * Создание соглашения
 * POST /agreements/add
 * @param {Object} body
 * @param {number|string} [body.number] - Номер соглашения
 * @param {string} [body.status] - Статус (draft, active, ...)
 * @param {string} body.date_from - Дата начала выполнения (YYYY-MM-DD)
 * @param {string} body.date_to - Дата окончания выполнения (YYYY-MM-DD)
 * @param {string} body.performer_type - Тип исполнителя
 * @param {number} [body.company_id] - ID контрагента (для ИП/ТОО/ГПХ)
 * @param {number} [body.counterparty_id] - ID контрагента (для «Сотрудник на подряд» и др.)
 * @param {number} body.project_id - ID проекта
 * @param {number} body.price - Сумма соглашения
 * @param {number} [body.smeta_version] - Версия сметы
 * @param {number[]} [body.block_ids] - ID блоков по смете
 */
export const addAgreement = async (body) => {
  const response = await fetch(`${getAPIURL()}agreements/add`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    let detail = text;
    try {
      const json = JSON.parse(text);
      if (Array.isArray(json.detail)) {
        detail = json.detail
          .map((d) => {
            const loc = Array.isArray(d.loc) ? d.loc.join(".") : "";
            const msg = d.msg != null ? d.msg : String(d);
            return loc ? `${loc}: ${msg}` : msg;
          })
          .join("; ");
      } else if (typeof json.detail === "string") {
        detail = json.detail;
      } else if (json.message) {
        detail = json.message;
      }
    } catch (_) {}
    throw new Error(detail);
  }

  return response.json();
};

/**
 * Удаление соглашения
 * DELETE /agreements/{id}
 */
export const deleteAgreement = async (id) => {
  const response = await fetch(`${getAPIURL()}agreements/${id}`, {
    method: "DELETE",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    let detail = text;
    try {
      const json = JSON.parse(text);
      detail =
        json.detail ||
        json.message ||
        (Array.isArray(json.detail) ? json.detail.map((d) => d.msg).join(", ") : text);
    } catch (_) {}
    throw new Error(detail);
  }

  return response.json().catch(() => ({}));
};

/**
 * Отправить соглашение на согласование
 * POST /agreements/{agreement_id}/send-for-approval
 * @param {number} agreementId
 * @param {{ approver_ids: number[], comment?: string }} body
 */
export const sendAgreementForApproval = async (agreementId, body) => {
  const response = await fetch(
    `${getAPIURL()}agreements/${agreementId}/send-for-approval`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    const text = await response.text();
    let detail = text;
    try {
      const json = JSON.parse(text);
      if (Array.isArray(json.detail)) {
        detail = json.detail
          .map((d) => {
            const loc = Array.isArray(d.loc) ? d.loc.join(".") : "";
            const msg = d.msg != null ? d.msg : String(d);
            return loc ? `${loc}: ${msg}` : msg;
          })
          .join("; ");
      } else if (typeof json.detail === "string") {
        detail = json.detail;
      } else if (json.message) {
        detail = json.message;
      }
    } catch (_) {}
    throw new Error(detail);
  }

  return response.json();
};

/**
 * Текущий (активный) процесс согласования по соглашению
 * GET /agreements/{agreement_id}/approval
 * @returns {Promise<object|null>} null если активного процесса нет (404)
 */
export const getCurrentAgreementApproval = async (agreementId) => {
  const response = await fetch(
    `${getAPIURL()}agreements/${agreementId}/approval`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
    },
  );

  if (response.status === 404) return null;

  if (!response.ok) {
    const text = await response.text();
    let detail = text;
    try {
      const json = JSON.parse(text);
      detail =
        json.detail ||
        json.message ||
        (Array.isArray(json.detail)
          ? json.detail.map((d) => d.msg).join(", ")
          : text);
    } catch (_) {}
    throw new Error(detail);
  }

  return response.json();
};

/**
 * История согласований/подписаний по соглашению
 * GET /agreements/{agreement_id}/approvals
 */
export const getAgreementApprovals = async (agreementId) => {
  const response = await fetch(
    `${getAPIURL()}agreements/${agreementId}/approvals`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
    },
  );

  if (response.status === 404) return [];

  if (!response.ok) {
    const text = await response.text();
    let detail = text;
    try {
      const json = JSON.parse(text);
      detail =
        json.detail ||
        json.message ||
        (Array.isArray(json.detail)
          ? json.detail.map((d) => d.msg).join(", ")
          : text);
    } catch (_) {}
    throw new Error(detail);
  }

  const data = await response.json();
  return Array.isArray(data) ? data : data?.results ?? [];
};

/**
 * Согласования соглашений, ожидающие решения от указанного согласующего
 * GET /agreements/approvals/by-approver/{approver_id}
 * @param {number} approverId
 * @param {{ project_id?: number }} [params]
 */
export const getAgreementApprovalsByApprover = async (
  approverId,
  params = {},
) => {
  const searchParams = new URLSearchParams();
  if (params.project_id != null) {
    searchParams.set("project_id", String(params.project_id));
  }
  const query = searchParams.toString();
  const response = await fetch(
    `${getAPIURL()}agreements/approvals/by-approver/${approverId}${query ? `?${query}` : ""}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
    },
  );

  if (!response.ok) {
    const text = await response.text();
    let detail = text;
    try {
      const json = JSON.parse(text);
      const validationErrors = json?.details?.errors;
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        detail = validationErrors
          .map((e) => e.msg || JSON.stringify(e))
          .join("; ");
      } else {
        detail =
          json.detail ||
          json.message ||
          (Array.isArray(json.detail)
            ? json.detail.map((d) => d.msg).join(", ")
            : text);
      }
    } catch (_) {}
    throw new Error(detail);
  }

  const data = await response.json();
  return Array.isArray(data) ? data : data?.results ?? [];
};

/**
 * Получить процесс согласования по id
 * GET /agreements/approvals/{approval_id}
 */
export const getAgreementApprovalById = async (approvalId) => {
  const response = await fetch(
    `${getAPIURL()}agreements/approvals/${approvalId}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
    },
  );

  if (!response.ok) {
    const text = await response.text();
    let detail = text;
    try {
      const json = JSON.parse(text);
      detail =
        json.detail ||
        json.message ||
        (Array.isArray(json.detail)
          ? json.detail.map((d) => d.msg).join(", ")
          : text);
    } catch (_) {}
    throw new Error(detail);
  }

  return response.json();
};

/**
 * Решение по шагу согласования (согласовать / отклонить)
 * PATCH /agreements/{agreement_id}/approval/steps/{step_id}
 */
export const decideAgreementApprovalStep = async (
  agreementId,
  stepId,
  body,
) => {
  const response = await fetch(
    `${getAPIURL()}agreements/${agreementId}/approval/steps/${stepId}`,
    {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    const text = await response.text();
    let detail = text;
    try {
      const json = JSON.parse(text);
      if (Array.isArray(json.detail)) {
        detail = json.detail
          .map((d) => {
            const loc = Array.isArray(d.loc) ? d.loc.join(".") : "";
            const msg = d.msg != null ? d.msg : String(d);
            return loc ? `${loc}: ${msg}` : msg;
          })
          .join("; ");
      } else if (typeof json.detail === "string") {
        detail = json.detail;
      } else if (json.message) {
        detail = json.message;
      }
    } catch (_) {}
    throw new Error(detail);
  }

  return response.json();
};

/**
 * Удалить процесс согласования
 * DELETE /agreements/approvals/{approval_id}
 */
export const deleteAgreementApproval = async (approvalId) => {
  const response = await fetch(
    `${getAPIURL()}agreements/approvals/${approvalId}`,
    {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
    },
  );

  if (!response.ok) {
    const text = await response.text();
    let detail = text;
    try {
      const json = JSON.parse(text);
      detail =
        json.detail ||
        json.message ||
        (Array.isArray(json.detail)
          ? json.detail.map((d) => d.msg).join(", ")
          : text);
    } catch (_) {}
    throw new Error(detail);
  }

  return response.json().catch(() => ({}));
};
